import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import React, { useMemo } from 'react';
import LexicalReadOnlyMarkdownViewer from '../../../MarkdownEditor/Lexical/LexicalReadOnlyMarkdownViewer';
import { useExecutiveSummaryData } from '../ExecutiveSummary/Context';
import {
  getCoachArtifactNarrative,
  getCoachArtifactProvenance,
  isCoachArtifactActivity,
} from '../ExecutiveSummary/utils';
import { ActionPlanFeedbackControls } from './ActionPlanFeedbackControls';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.pxToRem(8),
    padding: theme.pxToRem(16),
    // Keep the draft from eating the whole pane so the editable action items
    // below stay visible; the narrative scrolls internally when it's long.
    flexShrink: 0,
    maxHeight: '50%',
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(1),
    marginBottom: theme.pxToRem(8),
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  feedback: {
    marginLeft: 'auto',
  },
  badge: {
    backgroundColor: theme.palette.blue[50],
    borderRadius: theme.pxToRem(4),
    color: theme.palette.blue[700],
    fontSize: theme.pxToRem(11),
    fontWeight: theme.typography.fontWeightBold,
    padding: `${theme.pxToRem(2)} ${theme.pxToRem(6)}`,
    textTransform: 'uppercase',
  },
  subtitle: {
    color: theme.palette.text.hint,
    fontSize: theme.pxToRem(12),
    flexShrink: 0,
    marginBottom: theme.pxToRem(12),
  },
  narrative: {
    flex: 1,
    fontSize: theme.pxToRem(14),
    minHeight: 0,
    overflowY: 'auto',
    overflowWrap: 'anywhere',
  },
}));

/**
 * Read-only card that renders the Zori Coach draft narrative from the member's
 * current care plan. The narrative lives on a special-cased, never-member-visible
 * CarePlan activity (see {@link isCoachArtifactActivity}); this card is
 * clinician-review-only — there is no publish/send/notify affordance, and the
 * activity is excluded from the editable action-item list.
 *
 * The envelope is parsed defensively (narrative-only in v1); a future `structured`
 * object is ignored here until matching renderers exist.
 */
export const ZoriCoachDraftCard: React.FC = () => {
  const classes = useStyles();
  const { executiveSummary } = useExecutiveSummaryData();

  const artifact = useMemo(
    () => executiveSummary?.carePlan?.activity?.find(isCoachArtifactActivity),
    [executiveSummary],
  );
  const narrative = useMemo(
    () => getCoachArtifactNarrative(artifact),
    [artifact],
  );
  const sourceRunId = useMemo(
    () => getCoachArtifactProvenance(artifact)?.sourceRunId,
    [artifact],
  );

  if (!narrative) {
    return null;
  }

  return (
    <Box
      className={classes.card}
      direction="column"
      data-testid="zori-coach-draft-card"
    >
      <div className={classes.header}>
        <Text className={classes.title}>Zori Coach draft</Text>
        <span className={classes.badge}>Draft · Not shared</span>
        <ActionPlanFeedbackControls
          className={classes.feedback}
          target="coach-narrative"
          sourceRunId={sourceRunId}
        />
      </div>
      <Text className={classes.subtitle}>
        AI-drafted coaching note for clinician review. Not visible to the
        member.
      </Text>
      <div className={classes.narrative}>
        <LexicalReadOnlyMarkdownViewer
          markdown={narrative}
          namespace="zori-coach-draft"
          syncKey={executiveSummary?.carePlan?.id ?? 'zori-coach-draft'}
        />
      </div>
    </Box>
  );
};

export default ZoriCoachDraftCard;
